/**
 * WhatsApp connection status from backend.
 * - checkChannelStatus: POST to channel API with { mobile_number, key: "whatsapp" } to get status (true/false).
 * - getWhatsappStatus: GET from REACT_APP_API_URL (optional fallback).
 */

const CHANNEL_STATUS_URL = 'https://acm5sxiiak.execute-api.us-east-1.amazonaws.com/prod/';

function getApiBaseUrl() {
  const url = (
    process.env.REACT_APP_API_URL ||
    ''
  ).replace(/\/$/, '');
  return url;
}

/**
 * Normalize phone number for query (strip + and spaces to match DynamoDB).
 * @param {string} phoneNumber
 * @returns {string}
 */
function normalizePhone(phoneNumber) {
  if (typeof phoneNumber !== 'string') return '';
  return phoneNumber.trim().replace(/^\+/, '').replace(/\s/g, '');
}

/**
 * Check WhatsApp channel status via POST. Sends { mobile_number, key: "whatsapp" }, returns true/false.
 * @param {string} mobileNumber - e.g. 94777651792
 * @returns {Promise<boolean>} - true if connected/available, false otherwise or on error
 */
async function checkChannelStatus(mobileNumber) {
  const mobile = normalizePhone(mobileNumber);
  if (!mobile) return false;
  try {
    const response = await fetch(CHANNEL_STATUS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile_number: mobile, key: 'whatsapp' }),
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => ({}));
    return data === true || data === 'true';
  } catch {
    return false;
  }
}

/**
 * Fetch WhatsApp connection status for a phone number (GET).
 * @param {string} phoneNumber - User's WhatsApp number (e.g. 94777651792)
 * @returns {Promise<{ is_connected: boolean }>} - { is_connected: true } or { is_connected: false }; on failure returns { is_connected: false }
 */
async function getWhatsappStatus(phoneNumber) {
  const base = getApiBaseUrl();
  if (!base) {
    return { is_connected: false };
  }
  const phone = normalizePhone(phoneNumber);
  if (!phone) {
    return { is_connected: false };
  }
  const url = `${base}/api/whatsapp/status?phone_number=${encodeURIComponent(phone)}`;
  try {
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
      return { is_connected: false };
    }
    const data = await response.json().catch(() => ({}));
    return {
      is_connected: data.is_connected === true,
    };
  } catch {
    return { is_connected: false };
  }
}

export { getWhatsappStatus, checkChannelStatus, normalizePhone };
